/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';
import { GridOverlay } from './grid_overlay';
import { FogPresentation } from './fog_presentation';
import { MapBackground } from './map_background';
import fogOfWarUrl from '../../assets/konva/fog_of_war.webp';
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants';

const FOG_TEXTURE_EDGE_PAD = 4;

export const StageMethods = {
  _newBackground() {
    this._backgroundError = null;
    const background = new MapBackground(this.imageUrlValue, this.imageContentTypeValue, {
      previewUrl: this.previewUrlValue,
      width: this.imageWidthValue,
      height: this.imageHeightValue,
      upgraded: (image) => {
        if (this._background !== background || !this._stage) return;
        this._imageLayer.findOne('Image').image(image);
        this._imageLayer.batchDraw();
      },
      redraw: () => {
        if (this._background === background) this._imageLayer?.batchDraw();
      },
      failed: () => {
        if (this._background === background) this._mapLoadFailed();
      }
    });
    this._background = background;
    return background;
  },

  _syncBackgroundStatus() {
    if (!this.hasBackgroundStatusTarget) return;
    this.backgroundStatusTarget.hidden = this.hiddenValue || !this._backgroundError;
    this.backgroundStatusTarget.textContent = this._backgroundError || '';
  },

  _mapLoadFailed() {
    this._disposeMapStage();
    this._backgroundError = 'Map could not load or play. Reload the page or use a supported file or browser.';
    this._syncBackgroundStatus();
  },

  _init() {
    delete this.element.dataset.konvaMapReady;

    if (!this.hasContainerTarget || !this.imageUrlValue || this.hiddenValue || document.hidden) return;
    const load = (this._mapLoad = (this._mapLoad || 0) + 1);
    const background = this._newBackground();
    this._syncBackgroundStatus();

    // Tool state
    this._tool = 'none';
    this._painting = false;

    // Pan/drag state
    this._dragging = false;
    this._dragStart = null;
    this._dragStartStagePos = null;

    // Fog state
    this._fogLayer = null;
    this._fogSignature = null;
    this._fogReady = false;
    this._fogTextureImage = null;
    this._rectStart = null;
    this._rectPreviewShape = null;

    // Zoom
    this._currentScale = 1.0;

    // Position manager state
    this._positionManagerActive = false;
    this._positionDragStart = null;
    this._positionStartPos = null;
    this._editorViewBeforePosition = null;
    clearTimeout(this._positionSaveDebounce);
    if (this._readonlyPositionAnimationFrame) {
      window.cancelAnimationFrame(this._readonlyPositionAnimationFrame);
      this._readonlyPositionAnimationFrame = null;
    }
    this._positionTransformTween?.destroy();
    this._positionTransformTween = null;

    // Shapes state
    this._shapesLayerBelow = null;
    this._shapesLayerAbove = null;
    this._shapes = [];
    this._shapeCounter = 0;
    this._selectedShapeColor = '#dc3545';
    this._pendingAreaEffect = null;
    this._areaEffectPreview = null;
    this._effectRotationHandle = null;
    this._effectRotating = false;

    // Select-tool state
    this._selectedShapeId = null;
    this._shapeDragStart = null;
    this._shapeDragOrigPos = null;
    this._shapeDragging = false;
    this._shapeMoved = false;
    this._shapeTransforming = false;
    this._shapeTransformer = null;

    // Edit modal state
    this._editingShapeId = null;

    Promise.all([background.load(), this._loadFogTextureImage()])
      .then(([img, fogTextureImage]) => {
        if (!this._mapConnected || load !== this._mapLoad) return;
        this._fogTextureImage = fogTextureImage;
        this._imgWidth = background.width;
        this._imgHeight = background.height;
        this._setupStage(img);

        // Shapes layer BELOW fog (between image and fog)
        this._shapesLayerBelow = new Konva.Layer();
        this._stage.add(this._shapesLayerBelow);

        // Single fog layer — full black coverage by default
        this._fogLayer = this._createFogLayer(true);
        this._fogPresentation = new FogPresentation(this._fogLayer.konvaLayer, {
          // The owner needs immediate brush feedback; audiences keep the fade.
          animate: this.readonlyValue || this.playerValue,
          fringe: this.readonlyValue || this.playerValue
        });

        // Shapes layer ABOVE fog (default for new shapes)
        this._shapesLayerAbove = new Konva.Layer();
        this._stage.add(this._shapesLayerAbove);

        this._shapeTransformer = new Konva.Transformer(SHAPE_TRANSFORMER_OPTIONS);
        this._shapeTransformer.on('transformstart', () => {
          this._finishShapeAnimations();
          this._shapeTransforming = true;
          this._shapeDragging = false;
          this._shapeMoved = false;
        });
        this._shapeTransformer.on('transformend', () => {
          this._shapeTransforming = false;
          if (this._cancellingGesture) return;
          this._commitShapeResize();
        });
        this._shapeTransformer.visible(false);
        this._shapesLayerAbove.add(this._shapeTransformer);

        if (this.canvasJsonValue) {
          try {
            this._restoreFromJson(JSON.parse(this.canvasJsonValue));
          } catch (e) {
            console.warn('Could not restore canvas JSON:', e);
          }
        }

        this._applyPositionWindowDimensions();
        this._initializeHistory?.();
        this._syncPositionManagerControls();

        if (this.readonlyValue) {
          this._applyPositionManagerTransform({ animate: true, useStoredStart: true });
        } else {
          this._applyTool(this._initialEditorTool());
          this._restoreEditorState();
        }

        this._gridOverlay = new GridOverlay(this);
        this._imageLayer.visible(true);
        this._stage.draw();
        this._fogReady = true;
        this.element.dataset.konvaMapReady = 'true';
        this._refreshEditModal();
        this._openRequestedCharacter();
        this._syncBackgroundStatus();
        if (!document.hidden) background.start();
      })
      .catch(() => {
        if (this._mapConnected && load === this._mapLoad) this._mapLoadFailed();
      });
  },

  _loadFogTextureImage() {
    if (!this.readonlyValue && !this.playerValue) return Promise.resolve(null);

    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = fogOfWarUrl;
    });
  },

  _setupStage(img) {
    const container = this.containerTarget;
    const viewport = this.hasViewportTarget ? this.viewportTarget : container.parentElement;

    const vw = viewport.clientWidth || this._imgWidth;
    const vh = viewport.clientHeight || Math.round(this._imgHeight * (vw / this._imgWidth));

    // Center the map using the initial viewport fit.
    const initScale = this._fitScale(vw, vh);
    this._currentScale = initScale;

    this._stage = new Konva.Stage({
      container,
      width: vw,
      height: vh,
      scaleX: initScale,
      scaleY: initScale,
      x: (vw - this._imgWidth * initScale) / 2,
      y: (vh - this._imgHeight * initScale) / 2
    });

    this._imageLayer = new Konva.Layer({ listening: false, visible: false });
    this._imageLayer.add(new Konva.Image({ image: img, x: 0, y: 0, width: this._imgWidth, height: this._imgHeight }));
    this._stage.add(this._imageLayer);
    this._updateZoomDisplay();
    this._syncFogBrushPopover();

    if (this._wheelViewport && this._handleWheel) this._wheelViewport.removeEventListener('wheel', this._handleWheel);
    this._handleWheel = this._onWheel.bind(this);
    viewport.addEventListener('wheel', this._handleWheel, { passive: false });
    this._wheelViewport = viewport;

    this._positionResizeObserver?.disconnect();
    if (window.ResizeObserver) {
      this._positionResizeObserver = new ResizeObserver(() => this._handleViewportResize());
      this._positionResizeObserver.observe(viewport);
    }

    if (this.readonlyValue) return;

    // Cursor overlay — always on top, never captures pointer events
    this._cursorLayer = new Konva.Layer({ listening: false });
    this._cursorCircle = new Konva.Circle({
      radius: this._fogBrushCursorRadius(),
      stroke: 'rgba(255,255,255,0.9)',
      strokeWidth: 2,
      strokeScaleEnabled: false,
      dash: [5, 4],
      fill: 'rgba(255,255,255,0.08)',
      visible: false,
      listening: false
    });
    this._cursorLayer.add(this._cursorCircle);
    this._stage.add(this._cursorLayer);

    this._connectCanvasTouch();
    this._stage.on('mousedown', (event) => {
      if (event.evt?.sourceCapabilities?.firesTouchEvents || this._touchGesture) return;
      this._usingTouch = false;
      this._beginCanvasGesture();
      this._onPointerDown(event);
    });
    this._stage.on('mousemove', (event) => {
      if (!event.evt?.sourceCapabilities?.firesTouchEvents && !this._touchGesture) this._onPointerMove();
    });
    this._stage.on('mouseup', (event) => {
      if (event.evt?.sourceCapabilities?.firesTouchEvents || this._touchGesture) return;
      this._onPointerUp();
      this._gestureSnapshot = null;
      this._rememberEditorState();
    });
    this._stage.on('mouseleave', () => this._onPointerLeave());
    if (this.workspaceValue) {
      this._stage.on('xChange.workspace yChange.workspace scaleXChange.workspace', () => this._positionSelectionToolbar());
    }
  },

  _createFogLayer(filled = true) {
    const coverage = this._fogCoverageAttrs();
    const konvaLayer = new Konva.Layer({ opacity: 1 });
    const group = new Konva.Group({ clip: coverage });
    konvaLayer.add(group);
    this._stage.add(konvaLayer);

    let baseRect = null;
    if (filled) {
      baseRect = new Konva.Rect(this._fogRectAttrs(coverage));
      group.add(baseRect);
    }

    // Surrounding fog belongs to the same silhouette, so it casts a shadow
    // inward onto revealed map edges as well as onto holes within the map.
    const outsideMap = this.readonlyValue || this.playerValue ? this._createOutsideMapFog() : null;
    if (outsideMap) konvaLayer.add(outsideMap);

    if (this.readonlyValue && this.fogOpacityValue < 1) {
      const canvas = konvaLayer.getCanvas()._canvas;
      if (canvas) canvas.style.opacity = this.fogOpacityValue;
    }

    this._syncFogOpacity(konvaLayer);
    if (this._cursorLayer) this._cursorLayer.moveToTop();
    return { konvaLayer, group, strokes: [], filled, baseRect, outsideMap };
  },

  _createOutsideMapFog() {
    return new Konva.Shape({
      ...this._fogRectAttrs(),
      sceneFunc: (context, shape) => {
        const scale = this._stage.scaleX();
        context.beginPath();
        context.rect(
          -this._stage.x() / scale,
          -this._stage.y() / scale,
          this._stage.width() / scale,
          this._stage.height() / scale
        );
        // Opposite winding cuts the actual map out of the viewport-sized fog.
        context.moveTo(0, 0);
        context.lineTo(0, this._imgHeight);
        context.lineTo(this._imgWidth, this._imgHeight);
        context.lineTo(this._imgWidth, 0);
        context.closePath();
        context.fillShape(shape);
      }
    });
  },

  _useFogTexture() {
    return (this.readonlyValue || this.playerValue) && this._fogTextureImage;
  },

  _fogCoverageAttrs() {
    const { width, height } = this._stageSize();
    const pad = this._useFogTexture() ? FOG_TEXTURE_EDGE_PAD : 0;

    return {
      x: -pad,
      y: -pad,
      width: width + pad * 2,
      height: height + pad * 2
    };
  },

  _fogRectAttrs(attrs = {}) {
    if (!this._useFogTexture()) return { fill: 'black', listening: false, ...attrs };
    const patternAttrs = this._fogPatternAttrs(attrs);

    return {
      fillPriority: 'pattern',
      fillPatternImage: this._fogTextureImage,
      fillPatternRepeat: 'repeat',
      ...patternAttrs,
      listening: false,
      ...attrs
    };
  },

  _fogPatternAttrs(attrs = {}) {
    const scale = this._stage?.scaleX() || 1;
    const stageX = this._stage?.x() || 0;
    const stageY = this._stage?.y() || 0;
    const x = attrs.x || 0;
    const y = attrs.y || 0;

    return {
      fillPatternScaleX: 1 / scale,
      fillPatternScaleY: 1 / scale,
      fillPatternX: -x - stageX / scale,
      fillPatternY: -y - stageY / scale
    };
  },

  _fogLineStroke() {
    if (!this._useFogTexture() || !this._fogLayer?.konvaLayer) return 'black';

    const canvas = this._fogLayer.konvaLayer.getCanvas()._canvas;
    const context = canvas?.getContext('2d');
    const pattern = context?.createPattern(this._fogTextureImage, 'repeat') || null;

    if (pattern?.setTransform) {
      const scale = this._stage?.scaleX() || 1;
      const stageX = this._stage?.x() || 0;
      const stageY = this._stage?.y() || 0;
      const matrix =
        typeof DOMMatrix === 'undefined'
          ? {
              a: 1 / scale,
              b: 0,
              c: 0,
              d: 1 / scale,
              e: -stageX / scale,
              f: -stageY / scale
            }
          : new DOMMatrix().translate(-stageX / scale, -stageY / scale).scale(1 / scale);
      pattern.setTransform(matrix);
    }

    return pattern || 'black';
  },

  _applyFogLineStroke(line, globalCompositeOperation = 'source-over') {
    line.setAttr('stroke', globalCompositeOperation === 'destination-out' ? 'black' : this._fogLineStroke());
  },

  _syncFogTextureTransform() {
    if (!this._useFogTexture() || !this._fogLayer) return;

    this._fogLayer.outsideMap?.setAttrs(this._fogPatternAttrs());

    if (this._fogLayer.baseRect) {
      this._fogLayer.baseRect.setAttrs(
        this._fogPatternAttrs({ x: this._fogLayer.baseRect.x(), y: this._fogLayer.baseRect.y() })
      );
    }

    this._fogLayer.strokes.forEach((shape) => {
      if (shape.globalCompositeOperation() === 'destination-out') return;

      if (shape instanceof Konva.Rect) {
        shape.setAttrs(this._fogPatternAttrs({ x: shape.x(), y: shape.y() }));
      } else if (shape instanceof Konva.Line) {
        this._applyFogLineStroke(shape, shape.globalCompositeOperation());
      }
    });

    this._fogLayer.konvaLayer.batchDraw();
  },

  _applyTool(tool) {
    if (this._gridCalibration) this._endGridCalibration({ restoreTool: false });
    if (tool !== 'select') this._clearCharacterMove?.();
    if (this.playerValue && !['shape-rect', 'character', 'area-effect', 'select', 'drag'].includes(tool)) tool = 'drag';
    if (tool !== 'character') {
      this._clearCharacterPreview?.();
      this._pendingCharacterToken = null;
    }
    if (tool !== 'area-effect') {
      this._clearAreaEffectPreview();
      this._pendingAreaEffect = null;
      this._areaEffectPlacementPoint = null;
    }
    if (this._tool === 'select' && tool !== 'select') this._deselectShape();
    this._tool = tool;

    // Fog pencil/eraser cursor circle
    if (this._cursorCircle) {
      if (tool === 'draw' || tool === 'erase') {
        this._updateFogBrushCursorSize();
        const worldPos = this._getWorldPointerPosition();
        if (worldPos) {
          this._cursorCircle.x(worldPos.x);
          this._cursorCircle.y(worldPos.y);
          this._cursorCircle.visible(true);
        }
      } else {
        this._cursorCircle.visible(false);
      }
      this._cursorLayer.batchDraw();
    }

    // Native cursor
    const cursorMap = { draw: 'none', erase: 'none', drag: 'grab', select: 'default', 'position-manager': 'grab' };
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = cursorMap[tool] || 'crosshair';

    // Button active states
    if (this.hasDragBtnTarget)
      this.dragBtnTarget.classList.toggle('active', tool === 'drag' || tool === 'position-manager');
    if (this.hasSelectBtnTarget) this.selectBtnTarget.classList.toggle('active', tool === 'select');
    this.element.querySelectorAll('[data-tool]').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tool === tool);
    });
    this._syncFogBrushPopover();
    this._syncPositionManagerMode(tool === 'position-manager');
    this._syncFogOpacity();
    this._syncMapTitle();
    this._syncMobileControls?.();
  }
};
